import { Shield, Users, Key, Lock, Zap } from "lucide-react";
import IdentityCapabilities from "./sections/identity-capabilities";
import AuthenticationFlow from "./sections/authentication-flow";
import AccountTypes from "./sections/account-types";
import SecurityPractices from "./sections/security-practices";
import CTASection from "../../components/cta-section";
import { useTranslation } from "../../i18n";

export default function NewIdentityRoute() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen overflow-x-hidden">
      {/* Gradient Background */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900" />

      {/* Diagonal White Section */}
      <div className="absolute top-0 left-0 right-0 h-[800px] -z-5">
        <div className="absolute top-0 left-0 right-0 h-full bg-white transform -skew-y-6 origin-top-left translate-y-[30%]" />
      </div>

      {/* Main Content */}
      <main className="relative z-10 px-4 md:px-8 pt-32 pb-24">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div>
            {" "}
            <h1 className="text-4xl md:text-6xl font-bold text-navy-900 leading-tight">
              {t('routes.identity.heroTitle')} {" "}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-violet-600 via-purple-600 to-orange-500">
                {t('routes.identity.heroHighlight')}
              </span>
            </h1>
            <p className="mt-6 text-gray-700 text-lg">
              {t('routes.identity.heroDescription')}
            </p>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-center">
                <div className="h-10 w-10 flex-shrink-0 mr-3 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shadow-lg">
                  <Key className="h-5 w-5 text-blue-600" />
                </div>
                <span className="text-sm font-medium">
                  {t('routes.identity.badges.universal')}
                </span>
              </div>
              <div className="flex items-center">
                <div className="h-10 w-10 flex-shrink-0 mr-3 rounded-xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center shadow-lg">
                  <Users className="h-5 w-5 text-indigo-600" />
                </div>
                <span className="text-sm font-medium">
                  {t('routes.identity.badges.multiTenant')}
                </span>
              </div>
              <div className="flex items-center">
                <div className="h-10 w-10 flex-shrink-0 mr-3 rounded-xl bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center shadow-lg">
                  <Lock className="h-5 w-5 text-purple-600" />
                </div>
                <span className="text-sm font-medium">
                  {t('routes.identity.badges.enterprise')}
                </span>
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <div className="relative">
              {/* Identity Visual Representation */}
              <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/20 p-8 max-w-md">
                <div className="text-center mb-6">
                  <div className="h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center mx-auto mb-4">
                    <Shield className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {t('routes.identity.card.title')}
                  </h3>
                  <p className="text-gray-600">
                    {t('routes.identity.card.description')}
                  </p>
                </div>

                <div className="space-y-3">
                  {[0, 1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="flex items-center p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300"
                    >
                      <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center mr-3">
                        <Zap className="h-4 w-4 text-white" />
                      </div>
                      <span className="text-sm font-medium text-gray-700">
                        {t(`routes.identity.card.services.${i}`)}
                      </span>
                      <div className="ml-auto">
                        <div className="h-2 w-2 rounded-full bg-green-500"></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Identity Capabilities */}
      <IdentityCapabilities />

      {/* Authentication & SSO */}
      <AuthenticationFlow />

      {/* Account Types */}
      <AccountTypes />

      {/* Security Practices */}
      <SecurityPractices />

      {/* CTA Section */}
      <CTASection />
    </div>
  );
}
